import ExclusiveReleaseBadge from "@/components/ui/ExclusiveReleaseBadge";
import HeartButton from "@/components/ui/HeartButton";
import { getIsExclusive } from "@/lib/config/album-overrides";
import { ListAlbumDto } from "@/lib/lutely-api/gen/dtos";
import { useLibrary } from "@/lib/lutely-api/hooks";
import { cn } from "@/lib/utils";
import { Box, Flex } from "@radix-ui/themes";
import { useState } from "react";
import { AlbumLink } from "../links/AlbumLink";
import { CreatorLink } from "../links/CreatorLink";

export type AlbumCardProps = {
  album: ListAlbumDto;
  className?: string;
};

function formatAlbumPrice(album: ListAlbumDto) {
  if (album.pricingModel === "PAY_WHAT_YOU_WANT") {
    if (album.price == null || album.price <= 0) {
      return "Free";
    }
    return `$${album.price.toFixed(2)}+`;
  }

  if (album.price == null || album.price <= 0) {
    return "Free";
  }

  return `$${album.price.toFixed(2)}`;
}

export default function AlbumCard({ album, className }: AlbumCardProps) {
  const [hovered, setHovered] = useState(false);
  const priceLabel = formatAlbumPrice(album);
  const isExclusive = getIsExclusive(album.albumId);
  const { isAlbumSaved } = useLibrary();
  const saved = isAlbumSaved(album.albumId);

  return (
    <Box>
      <Box
        className={cn("relative w-full aspect-square", className)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <AlbumLink
          album={album}
          aria-label={`View album ${album.title}`}
          className="block h-full w-full overflow-hidden rounded-lg border-none bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80 cursor-pointer"
        >
          <Box
            className="bg-no-repeat bg-center bg-cover h-full w-full object-cover rounded-lg"
            style={{
              backgroundImage: `url("${album.artworkUrl || "/placeholders/album_art_placeholder.png"}")`,
            }}
          />
        </AlbumLink>

        {isExclusive && (
          <ExclusiveReleaseBadge
            hoverExpand
            className="absolute top-2 left-2 z-10 scale-90"
          />
        )}

        {hovered && (
          <Flex
            className="pointer-events-none absolute top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.3)] rounded-xl"
            aria-hidden="true"
          />
        )}

        {(hovered || saved) && (
          <HeartButton
            kind="album"
            id={album.albumId}
            className="absolute !m-0 top-1 right-1 z-10 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)] hover:bg-black/25"
          />
        )}
      </Box>

      <Flex direction="column" className="mt-[5px] w-full">
        <Flex className="items-center justify-between gap-2">
          <AlbumLink
            album={album}
            className="font-semibold text-blue-900 text-[14px] truncate hover:underline min-w-0"
          >
            {album.title}
          </AlbumLink>

          <AlbumLink
            album={album}
            className="text-blue-500 font-semibold text-[14px] shrink-0"
          >
            {priceLabel}
          </AlbumLink>
        </Flex>

        <CreatorLink
          creator={album.creator}
          className="text-[#525966] font-semibold truncate text-[14px] hover:underline mt-[2px]"
        >
          {album.creator.stageName}
        </CreatorLink>
      </Flex>
    </Box>
  );
}
